/**
 * Security Manager for Dale365
 * Handles input sanitization, rate limiting, and security validations
 */

class SecurityManager {
  constructor() {
    this.rateLimiter = new Map();
    this.suspiciousActivity = new Map();
    this.csrfToken = null;
  }

  /**
   * Sanitize user input to prevent XSS attacks
   * @param {string|any} input - Input to sanitize
   * @returns {string|any} Sanitized input
   */
  sanitizeInput(input) {
    if (typeof input !== 'string') return input;
    
    // Remove dangerous HTML tags and attributes
    return input
      // Remove script tags
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      // Remove on* event handlers
      .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
      // Remove javascript: protocol
      .replace(/javascript:/gi, '')
      // Remove data: protocol for potentially dangerous content
      .replace(/data:text\/html/gi, '')
      // Escape HTML entities
      .replace(/[<>]/g, (tag) => {
        const tags = {
          '<': '&lt;',
          '>': '&gt;'
        };
        return tags[tag] || tag;
      })
      // Remove SQL injection attempts
      .replace(/(\b(union|select|insert|update|delete|drop|exec|execute)\b)/gi, '')
      // Trim whitespace
      .trim();
  }

  /**
   * Sanitize monetary values
   * @param {string|number} value - Monetary value
   * @returns {string} Sanitized monetary value
   */
  sanitizeMonetaryValue(value) {
    // Convert to string and remove everything except digits and decimal point
    const cleaned = String(value).replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit to 2 decimal places
    if (parts.length === 2) {
      return parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    return cleaned;
  }

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} Is valid email
   */
  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate username format
   * @param {string} username - Username to validate
   * @returns {boolean} Is valid username
   */
  validateUsername(username) {
    // Allow alphanumeric, underscores, and hyphens
    const usernameRegex = /^[a-zA-Z0-9_-]{3,20}$/;
    return usernameRegex.test(username);
  }

  /**
   * Check rate limiting
   * @param {string} userId - User identifier
   * @param {string} action - Action being performed
   * @param {number} limit - Maximum attempts allowed
   * @param {number} window - Time window in milliseconds
   * @returns {boolean} Is action allowed
   */
  checkRateLimit(userId, action, limit = 10, window = 60000) {
    const key = `${userId}-${action}`;
    const now = Date.now();
    const attempts = this.rateLimiter.get(key) || [];
    
    // Clean old attempts
    const validAttempts = attempts.filter(time => now - time < window);
    
    if (validAttempts.length >= limit) {
      this.logSuspiciousActivity(userId, 'rate-limit-exceeded', {
        action,
        attempts: validAttempts.length,
        limit
      });
      return false;
    }
    
    validAttempts.push(now);
    this.rateLimiter.set(key, validAttempts);
    return true;
  }

  /**
   * Generate CSRF token
   * @returns {string} CSRF token
   */
  generateCSRFToken() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    this.csrfToken = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    
    // Store in meta tag and localStorage
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta) {
      meta.content = this.csrfToken;
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'csrf-token';
      newMeta.content = this.csrfToken;
      document.head.appendChild(newMeta);
    }
    
    localStorage.setItem('csrf-token', this.csrfToken);
    return this.csrfToken;
  }

  /**
   * Get CSRF token
   * @returns {string} CSRF token
   */
  getCSRFToken() {
    if (!this.csrfToken) {
      // Try to get from meta tag first
      const meta = document.querySelector('meta[name="csrf-token"]');
      if (meta && meta.content) {
        this.csrfToken = meta.content;
      } else {
        // Get from localStorage or generate new
        this.csrfToken = localStorage.getItem('csrf-token') || this.generateCSRFToken();
      }
    }
    return this.csrfToken;
  }

  /**
   * Validate CSRF token
   * @param {string} token - Token to validate
   * @returns {boolean} Is valid token
   */
  validateCSRFToken(token) {
    return token === this.getCSRFToken();
  }

  /**
   * Log suspicious activity
   * @param {string} userId - User identifier
   * @param {string} type - Type of suspicious activity
   * @param {Object} details - Additional details
   */
  logSuspiciousActivity(userId, type, details = {}) {
    const activity = {
      userId,
      type,
      details,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    // Add to in-memory log
    const userActivities = this.suspiciousActivity.get(userId) || [];
    userActivities.push(activity);
    
    // Keep only last 100 activities per user
    if (userActivities.length > 100) {
      userActivities.shift();
    }
    
    this.suspiciousActivity.set(userId, userActivities);
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('Suspicious activity detected:', activity);
    }
    
    // In production, send to monitoring service
    if (process.env.NODE_ENV === 'production') {
      this.sendToMonitoring(activity);
    }
  }

  /**
   * Send activity to monitoring service
   * @param {Object} activity - Activity to log
   */
  async sendToMonitoring(activity) {
    try {
      // This would send to your monitoring service
      // For now, just store in localStorage
      const logs = JSON.parse(localStorage.getItem('security-logs') || '[]');
      logs.push(activity);
      
      // Keep only last 50 logs
      if (logs.length > 50) {
        logs.splice(0, logs.length - 50);
      }
      
      localStorage.setItem('security-logs', JSON.stringify(logs));
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  }

  /**
   * Check if user is showing suspicious behavior
   * @param {string} userId - User identifier
   * @returns {boolean} Is suspicious
   */
  isSuspiciousUser(userId) {
    const activities = this.suspiciousActivity.get(userId) || [];
    const recentActivities = activities.filter(
      a => Date.now() - a.timestamp < 3600000 // Last hour
    );
    
    // Suspicious if more than 10 suspicious activities in last hour
    return recentActivities.length > 10;
  }

  /**
   * Validate password strength
   * @param {string} password - Password to validate
   * @returns {Object} Validation result
   */
  validatePassword(password) {
    const minLength = 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    const strength = {
      valid: password.length >= minLength,
      score: 0,
      feedback: []
    };
    
    if (password.length < minLength) {
      strength.feedback.push(`Mínimo ${minLength} caracteres`);
    } else {
      strength.score += 1;
    }
    
    if (!hasUppercase) {
      strength.feedback.push('Adicione letras maiúsculas');
    } else {
      strength.score += 1;
    }
    
    if (!hasLowercase) {
      strength.feedback.push('Adicione letras minúsculas');
    } else {
      strength.score += 1;
    }
    
    if (!hasNumber) {
      strength.feedback.push('Adicione números');
    } else {
      strength.score += 1;
    }
    
    if (!hasSpecial) {
      strength.feedback.push('Adicione caracteres especiais');
    } else {
      strength.score += 1;
    }
    
    // Set strength level
    if (strength.score <= 2) {
      strength.level = 'weak';
    } else if (strength.score <= 3) {
      strength.level = 'medium';
    } else {
      strength.level = 'strong';
    }
    
    return strength;
  }

  /**
   * Encrypt sensitive data for localStorage
   * @param {string} data - Data to encrypt
   * @param {string} key - Encryption key
   * @returns {string} Encrypted data
   */
  encryptData(data, key) {
    // Simple XOR encryption for demo - use proper encryption in production
    const keyHash = this.hashString(key);
    let encrypted = '';
    
    for (let i = 0; i < data.length; i++) {
      encrypted += String.fromCharCode(
        data.charCodeAt(i) ^ keyHash.charCodeAt(i % keyHash.length)
      );
    }
    
    return btoa(encrypted);
  }

  /**
   * Decrypt sensitive data from localStorage
   * @param {string} encrypted - Encrypted data
   * @param {string} key - Decryption key
   * @returns {string} Decrypted data
   */
  decryptData(encrypted, key) {
    try {
      const keyHash = this.hashString(key);
      const data = atob(encrypted);
      let decrypted = '';
      
      for (let i = 0; i < data.length; i++) {
        decrypted += String.fromCharCode(
          data.charCodeAt(i) ^ keyHash.charCodeAt(i % keyHash.length)
        );
      }
      
      return decrypted;
    } catch (error) {
      console.error('Decryption error:', error);
      return null;
    }
  }

  /**
   * Simple hash function for strings
   * @param {string} str - String to hash
   * @returns {string} Hash
   */
  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Clear all security data
   */
  clearSecurityData() {
    this.rateLimiter.clear();
    this.suspiciousActivity.clear();
    this.csrfToken = null;
    localStorage.removeItem('csrf-token');
    localStorage.removeItem('security-logs');
  }
}

// Export singleton instance
export default new SecurityManager();